import { createRef, useEffect, useState } from 'react'
import { MdCheckCircle, MdClose, MdLocationOn, MdPayment } from 'react-icons/md'
import PaymentOptions from '@/components/billing/payment-options'
import ServiceAddressSection, { ServiceAddress } from './service-address-section'

interface ApproveBudgetModalProps {
    isOpen: boolean
    onClose: () => void
    /**
     * Recebe o endereço confirmado e a forma de pagamento fechada com o paciente.
     *
     * A forma vem daqui porque é aqui que a cobrança nasce: escolhida na aprovação,
     * ela já sai com método e parcelamento, e o operador não precisa passar pela tela
     * de cobrança só para definir isso antes de enviar o link.
     */
    onConfirm: (
        address: ServiceAddress,
        payment?: { method: string; installments: number | null },
    ) => void | Promise<void>
    loading?: boolean
    /**
     * Valor do serviço em centavos, SEM taxa. Sem ele a simulação não roda e o modal
     * segue só com o local — é o comportamento do flex, que não gera cobrança.
     */
    amount?: number
    /** Flex não gera cobrança: a escolha de forma não faz sentido e some. */
    hidePayment?: boolean
    /**
     * Nome do convênio que cobre o atendimento, quando há um.
     *
     * A cobrança nasce faturada à operadora: sem gateway, sem link ao paciente e
     * sem taxa de meio de pagamento. Escolher forma de pagamento aqui não muda
     * nada e sugere ao operador que o paciente vai pagar — por isso o bloco some
     * e o texto diz quem paga. Ver docs/health-insurance-system.md §2.
     */
    healthPlanName?: string | null
    /** Do orçamento — de onde vêm os endereços oferecidos para confirmação. */
    partnerId: number | null
    professionalId: number | null
    hospitalId?: number | null
    /** "exame" | "procedimento" — usado nos textos. */
    entityLabel: string
}

/**
 * Aprovar o orçamento é o momento em que o atendimento passa a existir: é aqui
 * que o local precisa ser confirmado (o orçamento não tem local, nada foi
 * agendado ainda). Sem confirmação, o botão de aprovar fica bloqueado — o
 * endereço cadastrado do profissional costuma ser o residencial, não o do
 * consultório.
 *
 * "Confirmar" inclui responder que o local AINDA NÃO ESTÁ DEFINIDO: sem parceiro
 * nem profissional (o caso do pacote) não há endereço a herdar, e exigir um
 * travava a aprovação para sempre. O que a trava garante é a DECISÃO — endereço
 * escolhido, digitado, ou declarado indefinido —, nunca o silêncio.
 */
export default function ApproveBudgetModal({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    amount = 0,
    hidePayment = false,
    healthPlanName = null,
    partnerId,
    professionalId,
    hospitalId,
    entityLabel,
}: ApproveBudgetModalProps) {
    const modalRef = createRef<HTMLDialogElement>()
    const [address, setAddress] = useState<ServiceAddress | null>(null)
    const [payment, setPayment] = useState<{
        method: string
        installments: number | null
    } | null>(null)

    useEffect(() => {
        if (isOpen) {
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
            setAddress(null)
            setPayment(null)
        }
    }, [isOpen])

    return (
        <dialog className="modal z-50" ref={modalRef} onClose={onClose}>
            <div className="modal-box !max-w-3xl !mx-4 !p-0">
                <div className="flex items-center justify-between p-4 border-b border-base-300">
                    <h2 className="text-lg font-bold">Aprovar orçamento</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-sm btn-ghost btn-square"
                        disabled={loading}
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <p className="text-base-content/70">
                        {healthPlanName ? (
                            <>
                                Ao aprovar, o orçamento vira {entityLabel} e a
                                cobrança é faturada ao convênio{' '}
                                <span className="font-semibold">
                                    {healthPlanName}
                                </span>
                                {' '}— o paciente não recebe link de pagamento.
                                Confirme antes onde ele será atendido.
                            </>
                        ) : (
                            <>
                                Ao aprovar, o orçamento vira {entityLabel} e a
                                cobrança é criada automaticamente. Confirme antes
                                onde o paciente será atendido.
                            </>
                        )}
                    </p>

                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            <MdLocationOn className="text-xl text-secondary" />
                            <h3 className="font-semibold">
                                Local de atendimento
                            </h3>
                        </div>
                        <ServiceAddressSection
                            partnerId={partnerId}
                            professionalId={
                                professionalId ? String(professionalId) : ''
                            }
                            hospitalId={hospitalId ?? null}
                            value={address}
                            onChange={setAddress}
                        />
                    </div>

                    {/* Forma de pagamento: MESMO componente do agendamento e da
                        cobrança. Opcional — sem escolher, a cobrança nasce como
                        antes e o operador a define depois. */}
                    {!hidePayment && !healthPlanName && amount > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <MdPayment className="text-xl text-secondary" />
                                <h3 className="font-semibold">
                                    Forma de pagamento
                                </h3>
                            </div>
                            <PaymentOptions
                                amount={amount}
                                context="budget-approval"
                                selectedMethod={payment?.method ?? null}
                                selectedInstallments={
                                    payment?.installments ?? null
                                }
                                onSelect={option =>
                                    setPayment({
                                        method: option.method,
                                        installments:
                                            option.method === 'CREDIT_CARD' &&
                                            option.installments > 1
                                                ? option.installments
                                                : null,
                                    })
                                }
                                // Sem forma escolhida, a aprovação segue o fluxo
                                // antigo: cobrança sem método e ida à tela de
                                // pagamento, onde ficam dinheiro, maquininha e o
                                // split entre formas.
                                onCustomize={() => setPayment(null)}
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-4 border-t border-base-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-outline-light !rounded-xl"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            address && onConfirm(address, payment ?? undefined)
                        }
                        className="btn btn-success gap-2 !rounded-xl"
                        disabled={loading || !address}
                        title={
                            address
                                ? 'Aprovar orçamento'
                                : 'Confirme o local de atendimento para aprovar'
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdCheckCircle className="text-lg" />
                        )}
                        Aprovar
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    )
}
